const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8080/api/v1";

const REQUEST_TIMEOUT_MS = 30000;

function getToken(): string | undefined {
    if (typeof document === "undefined") return undefined;
    const match = document.cookie.match(/(?:^|;\s*)token=([^;]*)/);
    return match ? match[1] : undefined;
}

export class ApiError extends Error {
    status: number;
    constructor(message: string, status: number) {
        super(message);
        this.status = status;
    }
}

async function handleResponse<T>(res: Response): Promise<T> {
    if (res.status === 401) {
        if (typeof window !== "undefined") {
            window.location.href = "/login";
        }
        throw new ApiError("Unauthorized", 401);
    }

    if (!res.ok) {
        const text = await res.text();
        throw new ApiError(text || `Request failed (${res.status})`, res.status);
    }

    const text = await res.text();
    try {
        return JSON.parse(text);
    } catch {
        return text as unknown as T;
    }
}

function authHeaders(): Record<string, string> {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchWithTimeout(url: string, options: RequestInit): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
        return await fetch(url, { ...options, signal: controller.signal });
    } finally {
        clearTimeout(timeoutId);
    }
}

export const api = {
    get<T>(path: string): Promise<T> {
        return fetchWithTimeout(`${API_BASE_URL}${path}`, {
            headers: {
                ...authHeaders(),
            },
            credentials: "include",
            cache: "no-store",
        }).then((res) => handleResponse<T>(res));
    },

    post<T>(path: string, body?: unknown): Promise<T> {
        return fetchWithTimeout(`${API_BASE_URL}${path}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...authHeaders(),
            },
            credentials: "include",
            body: body ? JSON.stringify(body) : undefined,
        }).then((res) => handleResponse<T>(res));
    },

    put<T>(path: string, body?: unknown): Promise<T> {
        return fetchWithTimeout(`${API_BASE_URL}${path}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                ...authHeaders(),
            },
            credentials: "include",
            body: body ? JSON.stringify(body) : undefined,
        }).then((res) => handleResponse<T>(res));
    },

    delete<T>(path: string): Promise<T> {
        return fetchWithTimeout(`${API_BASE_URL}${path}`, {
            method: "DELETE",
            headers: {
                ...authHeaders(),
            },
            credentials: "include",
        }).then((res) => handleResponse<T>(res));
    },

    /** For unauthenticated endpoints (auth, public) */
    public: {
        post<T>(path: string, body?: unknown): Promise<T> {
            return fetchWithTimeout(`${API_BASE_URL}${path}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: body ? JSON.stringify(body) : undefined,
            }).then((res) => handleResponse<T>(res));
        },
    },
};
